import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {
  type AskSuggestion,
  type AskSuggestionKind,
  useAskSuggestions,
} from 'components/ai/useAskSuggestions';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';
import type {ReactNode} from 'react';
import {KnowledgeChipsContainer} from 'views/home/sections/Search';

import styles from './PeopleSearch.module.css';

/** Typeahead route for an Ask suggestion row; GlobalSearch asks it on selection. */
export const ASK_SUGGESTION_ROUTE = 'company_os_ai_suggestion';

/** The subtitle's topic, so an Ask row reads like any other result: what it's about. */
const TOPIC_KEY: Record<AskSuggestionKind, string> = {
  workstream: 'search_ask_topic_workstreams',
  workstream_update: 'search_ask_topic_workstreams',
  hr: 'search_ask_topic_hr',
  owner: 'search_ask_topic_owner',
  it: 'search_ask_topic_it',
  headcount: 'search_ask_topic_headcount',
};

const renderAskRow = (suggestion: AskSuggestion & {index: number}) => (
  <Typeahead.Row
    key={suggestion.question}
    value={{
      route: ASK_SUGGESTION_ROUTE,
      path: suggestion.question,
      label: suggestion.question,
      index: suggestion.index,
      suggestionKind: suggestion.kind,
    }}
    withLeftAccessory={<AvatarIcon src={CompanyAskGlyph} size="large" />}
    withTitle={<Text isBold>{suggestion.question}</Text>}
    withSubtitle={
      <Text size="small" color="faint">
        {t('search_ask_row_subtitle', {topic: t(TOPIC_KEY[suggestion.kind])})}
      </Text>
    }
    className={styles.typeaheadRow}
  />
);

/**
 * A section label in the empty search panel. DIG's results title is inset past
 * the rows' avatars and the topic chips, so every section uses this one instead
 * and they share a left edge. Spacing: a results list adds 8px above its rows
 * and 8px below them, so with the chips' padding below, every section is 24px
 * from the one before and 16px from its label.
 */
export const SearchSectionTitle = ({children}: {children: ReactNode}) => (
  <Box paddingX="8" paddingTop="8">
    <Text size="small" color="subtle" isBold>
      {children}
    </Text>
  </Box>
);

/**
 * The search panel before anything is typed: an Ask section, then the home
 * page's topic chips, then the suggested people and work (rendered by
 * GlobalSearch under its own section title). The Ask rows are real typeahead
 * results, so ↓ from the input reaches them, and they only exist with an empty
 * query, so they can't be mistaken for the person someone is looking up.
 */
export const SearchEmptyState = () => {
  const suggestions = useAskSuggestions();
  // A workstream that needs you, if one does, then the most common questions.
  const rows = suggestions.slice(0, 2).map((suggestion, index) => ({...suggestion, index}));

  return (
    <>
      <SearchSectionTitle>{t('search_section_ask')}</SearchSectionTitle>
      <Typeahead.Results size="large" results={rows} renderRow={renderAskRow} />
      <Box paddingBottom="16">
        <KnowledgeChipsContainer
          surface="search"
          title={
            <Box paddingBottom="16">
              <SearchSectionTitle>{t('search_section_popular')}</SearchSectionTitle>
            </Box>
          }
        />
      </Box>
    </>
  );
};
